import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  safeAsync, Badge, emptyMsg, neuShadow, btnSm
} from './crmShared';
import {
  PipelineHealthStrip, AdvanceSuggestionsBanner, CopilotChat,
} from './PipelineIntelligence';
import CrmFilterBar from '../../components/CrmFilterBar/CrmFilterBar';
import PipelineBoard from './PipelineBoard';
import PipelineLegacyBoard from './PipelineLegacyBoard';
import { readParam, writeParams } from '../../lib/urlState';
import {
  loadPipelineChoice, savePipelineChoice,
  loadBoardPlace, saveBoardPlace, resolvePipelineId, EMPTY_PLACE,
} from '../../lib/pipelinePlace';

/* ═══════════════════════════════════════════════════════════════════════════
   PipelineTab — ONE pipeline at a time, full width, with a selector.

   ⚠ THIS REPLACES THE ACCORDION (Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026,
   from Carlos's CRM 2.0 write-up of 9 Oct, item 2).

   WHAT THE ACCORDION DID, MEASURED: every pipeline returned by
   crm:pipelines:list rendered a header row, and one of them could be expanded
   to a board squeezed between the rows above and below it.
   `crm:pipelines:list` is `SELECT * FROM crm_pipelines WHERE is_active = TRUE`
   with no per-user filter, and PR #491's read-only production probe of
   2026-09-17 recorded ELEVEN rows all flagged active, of which TWO are live.
   So the working board sat inside a list of nine pipelines nobody works in.

   ⛔⛔ THIS CHANGES DISPLAY, NOT ACCESS. Every pipeline the server returns is
   still offered in the selector and still one click away; nothing is hidden,
   nothing is filtered, and no authority check is added or removed here. PR
   #491 separates SELECTION (pure UI state), AUTHORITY (must be unreachable
   when denied) and VISIBILITY (hidden must stay reachable); this is the first
   of the three and only the first. The server-side chokepoint #491 §4 calls
   for — the one that would make a denied pipeline unreachable by id — does not
   exist yet and is deliberately NOT started here: a selector that filtered the
   list would look like a security control while `invoke('crm:pipeline:board',
   <any id>)` still answered, which is the exact conflation that document warns
   against.

   WHERE THIS DEVIATES FROM #491, STATED RATHER THAN IMPLIED:

     • #491 §5 keeps a hide/show preference in `user_ui_preferences` through
       the `ui:preferences:*` channels. This card's selection lives in
       localStorage instead. They are different things — §5 is VISIBILITY (a
       list of pipelines suppressed from view, which must survive to any
       browser the person signs in from) and this is SELECTION, which §0 calls
       "pure UI state". Nothing here hides anything, so no preference needs to
       travel. When §5 is built it should take the server-side record, and the
       two must not be merged into one key.
     • #491 §5 requires any such control to carry a TEXT LABEL rather than a
       bare icon (issue #420: borderless icon buttons read as decoration). The
       selector below is a labelled <select> and the label is permanent.
   ═══════════════════════════════════════════════════════════════════════════ */

export default function PipelineTab({
  pipelines, contacts, allUsers, currentUser, canCreate, loadData, loadExtended, onContactClick, onContactPreview, stageMoves, userRole,
  // Card P114 (E3). Already supplied by CRMPage's sharedProps — only the
  // destructure was missing. See the Add Contact button below for why this
  // is a prop rather than a modal of its own.
  setShowAddContact,
}) {
  // Owner + free-text search are handed to the BOARD, which applies them
  // server-side. The board is paged, so filtering client-side would search
  // only the first page of each column.
  //
  // The bar's stage control is REMOVED here (hideStages), not left inert —
  // one column per stage makes it redundant, and a visible control that does
  // nothing is the same defect as a drag affordance that refuses.
  //
  // ⚠ THE BAR ALREADY SURVIVES THIS ROUND TRIP AND DID BEFORE THIS CARD —
  // measured, not assumed. CrmFilterBar hydrates owner/sort/date from the URL
  // (`fo`, `fsb`, `fsd`, `fdf`, `fdr`) and the free-text search from
  // tab-scoped sessionStorage, both on mount, so unmounting and remounting
  // this tab restores them. Nothing here re-implements that; this comment
  // exists so the next person does not add a second mechanism for it.
  const [barFilter, setBarFilter] = useState({ owner: null, search: '' });

  const userKey = (currentUser && currentUser.id) || 'anon';
  const list = useMemo(() => (Array.isArray(pipelines) ? pipelines : []), [pipelines]);

  /*
   * ── WHICH PIPELINE IS SHOWN, AND WHERE IN IT ─────────────────────────────
   *
   * ⛔⛔ THE ID AND ITS PLACE ARE ONE PIECE OF STATE, SET TOGETHER.
   *
   * They were two, and that was a real bug found by the round-trip suite
   * before it was fixed: the id resolved in one effect and the place loaded in
   * a second, so the board MOUNTED ONE RENDER EARLY with `place = null`. Its
   * restore latch — deliberately armed once per pipeline, so a later re-render
   * cannot yank someone back to where they were ten seconds ago — latched onto
   * that null and the scroll was never put back. The measurement was a silent
   * 0: no error, no warning, a board at the top of every column. Keeping them
   * in one state object makes "a board without its place" unrepresentable.
   *
   * ⚠ LATCHED TO THE FIRST RESOLUTION, like every other restore in this app
   * (Card PARTNER-LIST-REFRESH: without the latch a remount reopens whatever
   * the URL last held and the person's first click CLOSES what they opened).
   * Here the latch is `shown.id === ''`: the URL and the remembered choice are
   * consulted until something is shown, and never again.
   */
  const [shown, setShown] = useState({ id: '', place: EMPTY_PLACE });
  const chosen = shown.id;
  const initialUrl = useRef(readParam('pipeline'));

  // ── The place record ─────────────────────────────────────────────────────
  // Also held in a ref and written through, because a scroll event fires dozens
  // of times a second: re-rendering the board on each one would make the board
  // fight the scroll it is recording. The ref is the LIVE place; `shown.place`
  // is the one-shot the board restores FROM.
  const placeRef = useRef(EMPTY_PLACE);
  /*
   * ⛔⛔ WHICH PIPELINE placeRef DESCRIBES, TRAVELLING WITH IT.
   *
   * A place and the board it belongs to have to move together, and this is the
   * SECOND time in this file that separating them produced a silent wrong
   * answer. Measured before the fix: switching pipelines wrote the NEW board's
   * empty place over the OLD board's stored one. React runs an effect's
   * CLEANUP after the state change that invalidated it, so the save-on-the-way-
   * out cleanup below ran with the old pipeline id still in its closure and
   * `placeRef` already replaced by the new board's. It saved an empty record
   * under the key of the board the person had just left — the exact scroll
   * position they were coming back for, erased by the act of looking somewhere
   * else. Keying the save off the ref's OWNER rather than off `chosen` makes
   * the pairing impossible to get wrong.
   */
  const placeOwner = useRef('');
  const saveTimer = useRef(null);
  // The last value written to `?stage=`, so the writer fires on a CHANGE of
  // column rather than on every scroll event inside one.
  const lastStageWritten = useRef(readParam('stage'));

  /**
   * Show a pipeline, with the place stored for it.
   *
   * The URL's `?stage=` overrides the stored stage for the same reason the URL
   * wins everywhere else here: it is the only one of the two that can carry a
   * pasted link. It applies ONLY to the pipeline the URL itself named — a
   * `?stage=` left over from another board must not follow you into this one.
   */
  const show = useCallback((id, { useUrlStage } = {}) => {
    const next = String(id || '');
    if (!next) { setShown({ id: '', place: EMPTY_PLACE }); return; }
    const stored = loadBoardPlace(userKey, next);
    const urlStage = useUrlStage ? readParam('stage') : '';
    const place = urlStage ? { ...stored, stageId: urlStage } : stored;
    placeRef.current = place;
    placeOwner.current = next;
    /*
     * ⚠ WRITE THE RESTORED STAGE OUT, don't merely remember it.
     *
     * MEASURED in a real browser (review round 2): a board restored from
     * sessionStorage showed the right column and the right scroll, and the
     * address bar carried `?pipeline=` with NO `?stage=`. The reporter below
     * only writes on a CHANGE of column, and a restored stage equals the one
     * already remembered — so it never reached the URL and the stage was
     * carried by sessionStorage alone. That is tab-scoped and gone when the
     * tab closes, which makes the stage half of a shared link silently
     * missing. Writing it here keeps the address bar and the place record
     * saying the same thing from the first paint.
     */
    lastStageWritten.current = place.stageId || '';
    writeParams({ stage: place.stageId || '' });
    setShown({ id: next, place });
  }, [userKey]);

  useEffect(() => {
    if (chosen || list.length === 0) return;
    const fromUrl = initialUrl.current;
    const next = resolvePipelineId(list, fromUrl, loadPipelineChoice(userKey));
    // The stage in the URL belongs to the pipeline in the URL. Resolving to
    // the remembered or default pipeline instead means that stage is not ours.
    if (next) show(next, { useUrlStage: !!fromUrl && next === fromUrl });
  }, [chosen, list, userKey, show]);

  /*
   * ⚠ A SELECTED PIPELINE THAT LEAVES THE LIST MUST NOT LEAVE A BLANK SCREEN.
   * A pipeline can be deactivated, or simply disappear between two loads of
   * crm:pipelines:list. Re-resolve rather than render a board for an id the
   * server no longer offers.
   */
  useEffect(() => {
    if (!chosen || list.length === 0) return;
    if (list.some((p) => String(p.id) === String(chosen))) return;
    show(resolvePipelineId(list, '', ''));
  }, [chosen, list, show]);

  const active = useMemo(
    () => list.find((p) => String(p.id) === String(chosen)) || null,
    [list, chosen]
  );

  const flushPlace = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (placeOwner.current) saveBoardPlace(userKey, placeOwner.current, placeRef.current);
  }, [userKey]);

  const onPlaceChange = useCallback((fn) => {
    placeRef.current = fn(placeRef.current || EMPTY_PLACE);
    // Mirror the stage into the URL so a RELOAD lands on the same column, the
    // behaviour Card PARTNER-LIST-REFRESH gave the contact record. Pixel
    // offsets stay out of the address bar — they change on every wheel notch
    // and would rewrite the URL dozens of times a second for no gain.
    const stageId = placeRef.current.stageId || '';
    if (stageId !== lastStageWritten.current) {
      lastStageWritten.current = stageId;
      writeParams({ stage: stageId });
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      if (placeOwner.current) saveBoardPlace(userKey, placeOwner.current, placeRef.current);
    }, 250);
  }, [userKey]);

  /*
   * ⛔⛔ THE PLACE IS WRITTEN ON THE WAY OUT, AND THE WAY OUT IS AN UNMOUNT.
   * CRMPage renders this tab as `{!selectedContactId && tab === 'pipeline' &&
   * …}`, so clicking a card unmounts this component within the same commit.
   * The 250 ms debounce above would never fire. This cleanup is what makes the
   * round trip work at all; without it the scroll offsets are written only
   * when the person pauses for a quarter second first.
   */
  useEffect(() => flushPlace, [flushPlace]);

  const selectPipeline = useCallback((id) => {
    const next = String(id || '');
    if (!next || next === String(chosen)) return;
    // ⚠ FLUSH BEFORE show() REPLACES THE REF. This is the explicit half of the
    // pairing above: the board being LEFT is saved here, while placeRef still
    // describes it.
    flushPlace();
    savePipelineChoice(userKey, next);
    writeParams({ pipeline: next, stage: '' });
    show(next);
  }, [chosen, flushPlace, userKey, show]);

  // Mirror the resolved choice into the URL and the per-user memory. This runs
  // for the FIRST resolution too (the one nobody clicked), so a reload returns
  // to the board that was actually on screen rather than to the default.
  useEffect(() => {
    if (!chosen) return;
    writeParams({ pipeline: chosen });
    savePipelineChoice(userKey, chosen);
  }, [chosen, userKey]);

  // Board vs Legacy, remembered with the rest of the place. "Board" is the
  // default; "Legacy" keeps the old contact kanban (and its drag) reachable so
  // nobody loses a working affordance. Legacy retires when drag lands on the
  // new board, which is gated on scoping the zgx_touched_at latch.
  const view = (shown.place && shown.place.view) || 'board';
  const setView = useCallback((v) => {
    onPlaceChange((p) => ({ ...p, view: v }));
    setShown((s) => ({ ...s, place: { ...(s.place || EMPTY_PLACE), view: v } }));
  }, [onPlaceChange]);

  if (list.length === 0) {
    return <div style={emptyMsg}>No pipeline configured. Go to <strong>Setup</strong> tab to seed the default pipeline.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <PipelineSelector
        pipelines={list}
        value={chosen}
        onChange={selectPipeline}
        active={active}
      />

      <CrmFilterBar
        resource="contacts"
        hideStages
        allUsers={allUsers}
        currentUser={currentUser}
        onFilterChange={(f) => setBarFilter({ owner: f.owner || null, search: f.search || '' })}
      />

      {/*
        * ⛔ Card P235 — the strip is TOLD the scope. It used to receive only
        * `role` and fetch CRM-wide, so its tiles contradicted the pill
        * rendered a few lines above them.
        *
        * ⚠ The owner comes from the SAME filter bar the board reads, so the
        * tiles and the cards below them narrow together.
        */}
      <PipelineHealthStrip
        role={userRole}
        pipelineId={active ? active.id : null}
        pipelineName={active ? active.name : null}
        ownerId={barFilter.owner || null}
      />
      <AdvanceSuggestionsBanner role={userRole} onContactClick={onContactClick} />
      <CopilotChat role={userRole} />

      {active && (
        /* ⚠ key ON THE PIPELINE ID. Switching pipelines must give the board a
           fresh mount: its column scroll positions, paging and expanded
           terminal set all belong to the pipeline that was showing, and
           carrying them into the next one would restore the wrong place. */
        <div key={active.id} data-pipeline-panel data-pipeline-id={active.id}
          style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '12px 14px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <div role="group" aria-label="Board view" style={{ display: 'flex', gap: 6 }}>
              {[['board', 'Board'], ['legacy', 'Legacy']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} aria-pressed={view === v}
                  style={{
                    fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
                    border: '1px solid ' + (view === v ? '#2E8B98' : '#E2E8F0'),
                    background: view === v ? '#2E8B98' : '#F8FAFC',
                    color: view === v ? '#fff' : '#475569', cursor: 'pointer',
                  }}>{label}</button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            {/*
              ⛔⛔ ADD A LEAD FROM THE BOARD — Card P114 (E3).

              Ryland, 23 Oct: "no ability to manually add a new lead". Taken
              literally it was wrong — Contacts has Add Contact and Voice
              Intake, and the deal form has P107's inline "+ New contact".
              Taken as meant it was exactly right: there was NOTHING on the
              Pipeline board, which is the screen he works from all day. The
              only create control here was "Add Stage", which is a different
              job for a different person.

              ⛔⛔ ONE WRITER, AND THAT IS WHY THIS IS `setShowAddContact`
              RATHER THAN A MODAL. The AddContactModal is hosted once at page
              level (src/pages/CRMPage.js, under MODALS) and its `onCreated`
              already calls loadData(). Rendering a second copy here — or
              calling crm:contacts:create directly — would be a second create
              path to keep in step with validation, duplicate warnings and the
              referral-answer requirement. The board reaches for the SAME
              modal and the SAME channel the Contacts tab uses; this file adds
              a button, not a writer.

              ⚠ Placed BEFORE Add Stage: adding a lead is the frequent act and
              adding a stage the rare one, and Add Stage is a `prompt()`
              nobody should reach for by accident.
            */}
            {canCreate && typeof setShowAddContact === 'function' && (
              <button onClick={() => setShowAddContact(true)}
                style={{ ...btnSm, color: '#2E8B98', borderColor: '#e0e7ff' }}>
                <Plus size={12} /> Add Contact
              </button>
            )}
            {canCreate && (
              <button onClick={safeAsync(async () => {
                const name = prompt('Stage name:');
                if (!name) return;
                const color = prompt('Color (hex, e.g. #2E8B98):', '#2E8B98') || '#2E8B98';
                await window.zignagenixAPI.addPipelineStage(active.id, { name, color });
                loadExtended();
              })} style={{ ...btnSm, color: '#2E8B98', borderColor: '#e0e7ff' }}>
                <Plus size={12} /> Add Stage
              </button>
            )}
          </div>

          {view === 'board' ? (
            <PipelineBoard
              pipelineId={active.id}
              barFilter={barFilter}
              onContactClick={onContactPreview || onContactClick}
              place={shown.place}
              onPlaceChange={onPlaceChange}
            />
          ) : (
            <PipelineLegacyBoard
              pipeline={active}
              contacts={contacts}
              loadData={loadData}
              loadExtended={loadExtended}
              /*
               * ⚠ The legacy view gets the drawer too — staff lose their place
               * here identically, and this is still CRM > Pipeline. It is NOT
               * otherwise extended (its header forbids that): no Move stage is
               * offered, because this board moves crm_contacts.pipeline_stage,
               * which its own header records as changing nothing any report
               * counts. Its existing dragMovedRef guard already keeps a drag
               * from reading as a click, and that guard is left exactly alone.
               */
              onContactClick={onContactPreview || onContactClick}
              userRole={userRole}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The selector.
 *
 * ⚠ A LABELLED <select>, NOT AN ICON AND NOT A MENU. PR #491 §5 and issue #420
 * both land on the same requirement for this control: a borderless icon button
 * reads as decoration and people do not find it. The label is permanent text,
 * the control is always on screen, and switching is one interaction — so
 * choosing a pipeline is never a door that closes behind you.
 *
 * ⚠ EVERY PIPELINE THE SERVER RETURNED IS LISTED. This control does not
 * filter, sort out, or suppress anything: what `crm:pipelines:list` returns is
 * what it offers. Narrowing it would be VISIBILITY (#491 §5, not built) and
 * narrowing it on the server would be AUTHORITY (#491 §4, not built) — and a
 * selector that quietly did either would be a security control that is not one.
 *
 * The per-pipeline badges that used to sit on each accordion header move here:
 * the stage count, and the #468 opportunity count which renders UNKNOWN rather
 * than 0 for a pipeline with no ghl_pipeline_id, because "none" and "unseen"
 * must not look the same.
 */
export function PipelineSelector({ pipelines, value, onChange, active }) {
  const stages = (active && active.stages) || [];
  return (
    <div style={{
      background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow,
      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <label htmlFor="crm-pipeline-select" style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
        Pipeline
      </label>
      <select
        id="crm-pipeline-select"
        data-pipeline-select
        value={String(value || '')}
        onChange={(e) => onChange(e.target.value)}
        style={{
          fontSize: 13, fontWeight: 700, color: '#212529', padding: '6px 10px',
          borderRadius: 10, border: '1px solid #CBD5E1', background: '#fff',
          minWidth: 240, maxWidth: '100%', cursor: 'pointer',
        }}>
        {pipelines.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name}{p.is_default ? ' (default)' : ''}
          </option>
        ))}
      </select>

      {active && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge text={`${stages.length} stages`} color="#2E8B98" />
          {active.count_state === 'unknown_unmapped'
            ? <Badge text="not mapped to GHL" color="#F59E0B" />
            : <Badge text={`${active.open_opportunity_count ?? 0} open opportunities`} color="#62BB46" />}
          {active.is_default && <Badge text="default" color="#62BB46" />}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }} />
      <span style={{ fontSize: 11, color: '#6C757D' }}>
        {pipelines.length === 1
          ? '1 pipeline available'
          : `${pipelines.length} pipelines available — switch above`}
      </span>

      {active && active.description && (
        <div style={{ flexBasis: '100%', fontSize: 11, color: '#6C757D' }}>{active.description}</div>
      )}
    </div>
  );
}
